// Path A implied pricing for the CLOB (see docs/requirements/clob-implied-pricing).
//
// A spread or fly implies iff every outright leg is live-quoted in the same
// curve. Derivation is per broker (§2.1 of the rulebook): one leg from BGC and
// another from GFI is not a workable package. Best bid and best offer are
// chosen independently across brokers.
//
// Implieds are NEVER written into the source book — this file just returns
// synthetic levels the grid can splice into `orderBook.{bids,offers}` for
// display. Nothing here mutates store rows.

// Which outright section a spread/fly section imports its legs from. Every
// non-custom spread/fly variant on the CLOB maps to exactly one outright
// section on the same curve — pricing a fwd spread off spot legs (or vice
// versa) would be silently wrong, so this map is the single source of truth.
// Custom / diff-start-date sections are absent: their legs are not plain
// outrights and need dedicated parsing (out of Phase 2 scope).
export const SPREAD_TO_OUTRIGHT_TYPE = {
    spreadFly: 'outright',
    fwdSpreadFly: 'fwd',
    audDcSpreadFly: 'outright',
    bobSpreadFly: 'bob',
    sixsSpreadFly: 'sixs',
    meetingDatesSpread: 'meetingDates',
    // Custom (different-start-date) sections. Their legs are arbitrary-start
    // forwards ("1w-6m X spot-1w"), not plain outrights. They source from the
    // fwd outright section on the same curve, which for fwd variants is keyed
    // by composite START|END (see legKey / useCompositeKey). Match-only for
    // now: a leg implies iff a fwd row with the same start+end is live-quoted.
    // Forward-rate derivation from spot points is intentionally out of scope.
    custom: 'fwd',
    bobCustom: 'bobFwd',
    sixsCustom: 'sixsFwd'
};
const SPREAD_FLY_TYPES = new Set(Object.keys(SPREAD_TO_OUTRIGHT_TYPE));
const CUSTOM_TYPES = new Set(['custom', 'bobCustom', 'sixsCustom']);
// Fwd variants can have multiple IMM starts on the same curve (Z26, H27, …),
// so `endLabel` alone collapses `Z26|1Y` and `H27|1Y` into one bucket and any
// fwd spread whose end matches gets a spurious implied. Key composite instead.
const COMPOSITE_KEY_TYPES = new Set(['fwd', 'bobFwd', 'sixsFwd']);
const IMPLIED_TARGET_TYPES = SPREAD_FLY_TYPES;

// Inlined (not imported) to avoid a module cycle with irs-clob-grid.helper —
// that file already imports withImplied. Mirrors the shape of `isLivePrice`
// there: a level is live unless deleted-view, traded-down-to-zero, or past its
// expiresAt.
const isLivePrice = (item, serverTime) => {
    if (!item) return false;
    if (item.traded && Number(item.dv01 ?? item.size) === 0) return false;
    // CCIL feed rows arrive with a server-computed live/stale expiry, so a stale feed price is never an implied source.
    if (item.expiresAt) {
        const now = new Date(item.serverTime || serverTime || Date.now()).getTime();
        if (new Date(item.expiresAt).getTime() <= now) return false;
    }
    return true;
};

// Tenor label -> years. 18M = 1.5, 6M = 0.5, otherwise strip trailing letter.
const yrs = (t) => {
    const s = String(t || '').trim().toUpperCase();
    if (!s) return 0;
    if (s.endsWith('M')) return parseFloat(s) / 12;
    return parseFloat(s) || 0;
};

const norm = (t) => String(t || '').replace(/\s+/g, '').toUpperCase();

// Mirror of lib/constants.addSpaceBeforeUnit — the FORM emit format for a
// tenor label. Store rows carry "6 M" (with the space); IRS forms
// (SpreadTab.getEndDate) split on " " to extract duration + unit and crash
// when the unit is missing. Emitting the spaced form from the start keeps
// synthetic rows shape-compatible with real store rows everywhere they hit
// downstream code.
const spacedTenor = (t) => {
    const s = String(t || '').trim();
    return s.replace(/^(\d*\.?\d+)\s*([DdMmYyWw])$/, (_, num, unit) => `${num} ${unit.toUpperCase()}`);
};

// Sort by increasing years: for a spread, [near, far]; for a fly, [near, belly, far].
const sortLegs = (labels) => labels
    .filter(Boolean)
    .map(norm)
    .sort((a, b) => yrs(a) - yrs(b));

// Path B vector key. Vectors are `{legKey: coefficient}`. Zero entries are
// dropped so equal vectors serialise identically regardless of how they were
// built up.
const vecKey = (v) => {
    const keys = Object.keys(v)
        .filter((k) => Math.abs(v[k]) > 1e-9)
        .sort();
    if (!keys.length) return null;
    return keys.map((k) => `${k}:${(+v[k].toFixed(6))}`).join(',');
};

// Meeting-date rows key on the PERIOD LABEL (e.g. "SEP26 RBA"), not a tenor —
// their `endLabel` is the period's END DATE (dd/MM/yy) and every row on the
// same period would collide if we keyed on that. A meeting-date SPREAD is
// { near_period, far_period } carried as `startLabel` (near) and `start1Label`
// (far), with end1Label/end2Label both null — different from tenor spreads.
// See app/resources/irs-clob/helpers/irs-clob-shape.js (scaffoldMeetingBond).
const OUTRIGHT_LEG_TYPES = new Set(['outright', 'fwd', 'meetingDates', 'bob', 'sixs']);

const legKey = (row, sectionType) => {
    if (sectionType === 'meetingDates') {
        // Stub and Full Dt share the same period startLabel but trade as two
        // distinct standalone instruments — pricing one must NOT imply the
        // other. Keep mdType in the key so their leg identities stay separate.
        const s = norm(row?.startLabel);
        if (!s) return null;
        return row?.mdType ? `${s}|${row.mdType}` : s;
    }
    if (COMPOSITE_KEY_TYPES.has(sectionType)) {
        const s = norm(row?.startLabel);
        const e = norm(row?.endLabel);
        return s && e ? `${s}|${e}` : null;
    }
    return norm(row?.endLabel);
};

// Weight vector for a row. Outright: {t:+1}. Tenor spread: {near:-1, far:+1}
// (longer tenor positive). Tenor fly: {near:-1, belly:+2, far:-1}. Meeting
// spread: {near_period:-1, far_period:+1} (chronological, given by the row).
// Returns null for anything this phase does not handle (custom, basis…).
export const instrumentLegs = (row, sectionType) => {
    if (!row) return null;
    if (OUTRIGHT_LEG_TYPES.has(sectionType)) {
        const t = legKey(row, sectionType);
        return t ? { [t]: 1 } : null;
    }
    if (CUSTOM_TYPES.has(sectionType)) {
        // Two arbitrary-start-date legs, each keyed by composite START|END so
        // the same tenor at different starts doesn't collide. Sign follows
        // the store convention used elsewhere (meeting spreads, etc.):
        // {startLabel,endLabel} is leg 1 at -1, {start1Label,end1Label} is
        // leg 2 at +1.
        const s0 = norm(row.startLabel);
        const e0 = norm(row.endLabel);
        const s1 = norm(row.start1Label);
        const e1 = norm(row.end1Label);
        if (!s0 || !e0 || !s1 || !e1) return null;
        const leg1 = `${s0}|${e0}`;
        const leg2 = `${s1}|${e1}`;
        if (leg1 === leg2) return null;
        return { [leg1]: -1, [leg2]: 1 };
    }
    if (!SPREAD_FLY_TYPES.has(sectionType)) return null;
    if (sectionType === 'meetingDatesSpread') {
        // Meeting spreads are always 2 legs, given directly by the row — no
        // sorting needed and no belly case (flies of meetings aren't quoted).
        const near = norm(row.startLabel);
        const far = norm(row.start1Label);
        if (!near || !far || near === far) return null;
        return { [near]: -1, [far]: 1 };
    }
    const legs = sortLegs([row.end1Label, row.end2Label, row.endLabel]);
    // Fwd spreads/flies: both legs share the row's IMM start. Compose
    // START|END so a Z26 spread matches only Z26-start outrights, not any
    // fwd row that happens to share the tenor.
    if (sectionType === 'fwdSpreadFly') {
        const s = norm(row.startLabel);
        if (!s) return null;
        if (legs.length === 2) return { [`${s}|${legs[0]}`]: -1, [`${s}|${legs[1]}`]: 1 };
        if (legs.length === 3) return { [`${s}|${legs[0]}`]: -1, [`${s}|${legs[1]}`]: 2, [`${s}|${legs[2]}`]: -1 };
        return null;
    }
    if (legs.length === 2) return { [legs[0]]: -1, [legs[1]]: 1 };
    if (legs.length === 3) return { [legs[0]]: -1, [legs[1]]: 2, [legs[2]]: -1 };
    return null;
};

// Directional rounding to a grid step (§5.3). bids floor, offers ceil. `dp` is
// the display precision (structures: 3, outrights: 5).
export const roundDir = (value, dp, side) => {
    if (!Number.isFinite(value)) return null;
    const step = 10 ** dp;
    // Snap out floating-point jitter (4.429 - 4.20525 → 22.374999999996)
    // before floor/ceil, else an exact grid value falls a tick the wrong way.
    // 10 extra decimals is well below any real price precision.
    const snap = Math.round(value * step * 1e10) / 1e10;
    const rounded = side === 'bids' ? Math.floor(snap) : Math.ceil(snap);
    return (rounded / step).toFixed(dp);
};

// Best price level per broker for one side. All same-priced orders from that
// broker aggregate (§2.3); other brokers do not join. Returns
// Map<bkr, { price, size, levels }>. `bids` -> highest price is best; `offers`
// -> lowest.
export const bestLevelByBroker = (levels, side, serverTime) => {
    const byBroker = new Map();
    const better = side === 'bids'
        ? (a, b) => a > b
        : (a, b) => a < b;
    (levels || []).forEach((level) => {
        if (!isLivePrice(level, serverTime)) return;
        // Implieds are never sources (§2). An implied level injected into an
        // outright row (e.g. the basis-panel enrichment) must not feed another
        // derivation — that would produce implieds-of-implieds and let stale
        // liquidity multiply.
        if (level.implied) return;
        // CCIL feed rows carry no broker attribution — bucket them under a
        // synthetic 'CCIL' broker so they can participate in implieds. The
        // resulting implied is flagged noTrade downstream so hit/lift is
        // blocked (there is no counterparty to book against).
        const bkr = level.source === 'CCIL' ? 'CCIL' : level.bkr;
        if (!bkr) return;
        const price = Number(level.price);
        const size = Number(level.dv01 ?? level.size ?? 0);
        if (!Number.isFinite(price) || size <= 0) return;
        const cur = byBroker.get(bkr);
        if (!cur || better(price, cur.price)) {
            byBroker.set(bkr, { price, size, levels: [level] });
        } else if (price === cur.price) {
            cur.size += size;
            cur.levels.push(level);
        }
    });
    return byBroker;
};

// One per-broker best table per book side per pricing pass: every recipe consuming the
// same levels reuses it instead of rescanning them. Broker visibility applied here too.
const bestTables = new WeakMap();
const visibleBestByBroker = (levels, side, ctx) => {
    if (!levels.length) return new Map();
    const key = `${ctx.serverTime}|${ctx.viewerCompanyId ?? ''}`;
    const cached = bestTables.get(levels);
    if (cached?.key === key) return cached.best;
    let best = bestLevelByBroker(levels, side, ctx.serverTime);
    // Broker viewer: strip buckets whose underlying level does not belong
    // to the viewer's own company. §2.2 broker visibility — a broker must
    // not derive implieds off another firm's legs even if the socket feed
    // happens to carry them. Trader viewer (viewerCompanyId null) sees
    // every broker's bucket.
    if (ctx.viewerCompanyId) {
        best = new Map([...best].filter(([, bucket]) => bucket.levels[0]?.companyId === ctx.viewerCompanyId));
    }
    bestTables.set(levels, { key, best });
    return best;
};

// Structure grid: 3 decimals. Outright grid: 5 decimals.
// Every outright variant (spot, fwd, meeting, bob, sixs) quotes in percent to
// 5dp; only structures (spread/fly/meeting-spread) quote bp to 3dp.
const dpFor = (sectionType) => (OUTRIGHT_LEG_TYPES.has(sectionType) ? 5 : 3);

// Structures quote in basis points, outrights in percent. One bp = 0.01 percent.
// Path A composes outrights (percent) into a structure (bp), so the sum needs
// scaling by 100. For an outright target this factor is 1.
const unitFor = (sectionType) => (sectionType === 'outright' ? 1 : 100);

// Price ONE recipe on ONE side. A recipe entry is `{ id, label, book, c }` —
// an instrument (outright or spread/fly) plus the coefficient it appears at
// in the recipe. Returns `{ raw, size, bkr, legs, bindingId }` or null.
//
// This is the shared engine step behind both Path A (native decomposition,
// where every recipe entry is an outright) and Path B (single-instrument
// scale or two-instrument combo, where entries can be any live instrument
// including spreads and flies).
// `books` resolves a cached Path B entry (which holds no book) to this build's book.
const priceRecipe = (recipe, side, targetSectionType, ctx, books = null) => {
    // Two sub-sums — outright sources (percent) and structure sources (bp)
    // — kept apart until the end, then combined into the target's unit.
    // Summing in each source's native unit before converting is what lets
    // exact grid values like 4.500 - 4.110 = 0.390 keep their precision;
    // scaling every source by 100 first would put fp noise into the sum.
    // Meeting/fwd/bob/sixs outright targets quote in percent just like spot —
    // targetIsBp only when the target is a spread/fly/meeting-spread.
    const targetIsBp = !OUTRIGHT_LEG_TYPES.has(targetSectionType);
    // Basis curves (e.g. THBNDD) quote outrights in bp already, not percent —
    // spread/fly and outright on that curve share one unit, so no *100 / /100
    // scaling is needed either direction. ctx.outrightsInBp is set per-group
    // by the caller for basis IRS groups.
    const combine = ctx.outrightsInBp
        ? (outrightSum, structureSum) => outrightSum + structureSum
        : (outrightSum, structureSum) => (
            targetIsBp ? outrightSum * 100 + structureSum : outrightSum + structureSum / 100
        );

    // For every entry, cache the per-broker best on the side this entry is
    // consumed. A negative coefficient flips the side (buy the target, sell
    // this leg).
    const entryMaps = recipe.map((entry) => {
        const consumeSide = (entry.c > 0) === (side === 'bids') ? 'bids' : 'offers';
        const levels = (entry.book ?? books?.[entry.ix])?.[consumeSide] || [];
        return visibleBestByBroker(levels, consumeSide, ctx);
    });
    if (entryMaps.some((m) => !m || m.size === 0)) return null;

    // Only brokers that appear in EVERY entry can work the whole recipe.
    const brokers = [...entryMaps[0].keys()].filter((bk) => entryMaps.every((m) => m.has(bk)));
    if (!brokers.length) return null;

    const winners = new Map();
    brokers.forEach((bkr) => {
        let outrightSum = 0;
        let structureSum = 0;
        let capacity = Infinity;
        let bindingId = null;
        const bests = recipe.map((entry, ix) => {
            const best = entryMaps[ix].get(bkr);
            const term = entry.c * best.price;
            if (entry.sourceIsOutright) outrightSum += term;
            else structureSum += term;
            const legCap = best.size / Math.abs(entry.c);
            if (legCap < capacity) {
                capacity = legCap;
                bindingId = entry.id;
            }
            return best;
        });
        const size = Math.floor(capacity);
        if (size < 1) return;
        // Leg detail is built only for the level that gets rendered (winnerLegs).
        winners.set(bkr, { raw: combine(outrightSum, structureSum), size, bkr, bindingId, recipe, bests, side });
    });
    return winners;
};

// The source legs behind one priced winner, for the derivation panel and execution.
const winnerLegs = ({ recipe, bests, bkr, bindingId, side }) => recipe.map((entry, ix) => {
    const best = bests[ix];
    const srcLevel = best.levels && best.levels[0];
    return {
        id: entry.id,
        tenor: entry.label,
        curve: entry.curve || null,
        weight: entry.c,
        side: (entry.c > 0) === (side === 'bids') ? 'bids' : 'offers',
        price: best.price,
        size: best.size,
        bkr,
        // Source section type — the panel formats the leg label
        // differently for meeting (period name) and fwd/custom
        // (composite START|END).
        srcSectionType: entry.sectionType || null,
        // Source notional (per aggregated top-of-book) + its total
        // dv01, so the size hover can pro-rate to the amount actually
        // consumed at parent size.
        srcNotional: srcLevel?.size ?? null,
        srcDv01Total: best.size,
        // Only populate when there IS a trader — srcLevel.tdr itself
        // doubles as the submitter's code on self chips, which would
        // mislabel the leg's party.
        tdr: srcLevel?.trader ? srcLevel.tdr : null,
        trader: srcLevel?.trader?.name || null,
        traderId: srcLevel?.traderId || srcLevel?.trader?.id,
        traderCompanyId: srcLevel?.userCompanyId,
        // Own-price detection at the cell — needs the same fields
        // isOwnPrice / isViewerLevel read.
        userId: srcLevel?.userId ?? null,
        companyId: srcLevel?.companyId ?? null,
        source: srcLevel?.source ?? null,
        // Actual bond_prices rows behind this aggregated top-of-book
        // level (§2.3 — same broker, same price, sizes summed). Phase 4
        // execution walks these FIFO, consuming each id in turn until
        // the leg's parent share is exhausted.
        bondPrices: (best.levels || []).map((lv) => ({
            id: lv.id,
            size: Number(lv.dv01 ?? lv.size ?? 0)
        })),
        binding: entry.id === bindingId
    };
});

// Shape the raw priceRecipe result into the synthetic level the grid renders.
// Unit conversion is done inside priceRecipe (per-entry) so this just rounds
// and packages.
// Deterministic id for the exact implied level. Keys on the WINNING recipe's
// leg identities (stable across partial-fill aggregation changes) + broker +
// side. Keying on child bond_price ids drifts every time a fill drops a fully-
// consumed row out of the top-of-book aggregate, which changes the uid mid-
// trade and makes flash rendering resurrect a "ghost" duplicate row on the
// same structure. Leg keys are stable so long as the same legs still win.
// Side is included so a bid implied and offer implied on the same structure
// don't collide (they'd naturally use opposite leg sides, but this is
// belt-and-braces). Broker keeps two broker-specific derivations distinct.
export const impliedUidFor = (winner, side) => {
    if (!winner) return null;
    const legIds = (winner.legs || [])
        .map((l) => l.id)
        .filter(Boolean)
        .slice()
        .sort();
    if (!legIds.length) return null;
    return [side || '', winner.bkr || '', ...legIds].join('|');
};

const shapeLevel = (priced, sectionType, side) => {
    if (!priced) return null;
    const winner = priced.legs ? priced : { ...priced, legs: winnerLegs(priced) };
    const price = roundDir(winner.raw, dpFor(sectionType), side);
    if (price === null) return null;
    // CCIL-sourced implied: no counterparty to book against, so mark noTrade
    // and stamp source='CCIL' so the Bkr cell renders the CCI chip.
    const isCcil = winner.bkr === 'CCIL';
    return {
        implied: true,
        type: side === 'bids' ? 'Bid' : 'Offer',
        price,
        size: winner.size,
        dv01: winner.size,
        bkr: winner.bkr,
        bkrName: null,
        companyId: null,
        traderId: null,
        tdr: null,
        trader: null,
        updatedAt: null,
        impliedUid: impliedUidFor(winner, side),
        expiresAt: null,
        legs: winner.legs,
        bindingId: winner.bindingId,
        bindingTenor: winner.legs.find((l) => l.binding)?.tenor || null,
        ...(isCcil ? { source: 'CCIL', noTrade: true } : {})
    };
};

// Path A: native decomposition. Every leg of the target is an outright. Fast
// path — one recipe, priced once. Signature preserved for existing callers.
const impliedForSide = (legVec, outrightBooks, side, sectionType, ctx) => {
    const tenors = Object.keys(legVec);
    const recipe = [];
    // Native Path A sources are always outrights — resolve the outright
    // family so leg labels can be formatted for meeting / fwd variants.
    const outrightFamily = SPREAD_TO_OUTRIGHT_TYPE[sectionType] || sectionType;
    for (const t of tenors) {
        const book = outrightBooks.get(t);
        if (!book) return [];
        // Basis legVec keys are `d:<tenor>` / `nd:<tenor>` so the sibling book
        // lookup can distinguish D from ND; the leg's display label is the
        // plain tenor + the curve code, so size pro-rate (findSourceLeg) can
        // still match on the row's plain tenor.
        const m = t.match(/^(d|nd):/i);
        const label = m ? t.slice(m[0].length) : t;
        const curve = m ? m[1].toUpperCase() : null;
        recipe.push({ id: `t:${t}`, label, curve, book, c: legVec[t], sourceIsOutright: true, sectionType: outrightFamily });
    }
    const winners = priceRecipe(recipe, side, sectionType, ctx);
    return shapePerBroker(winners, sectionType, side);
};

// Path B: try every indexed combo that reaches the target vector. A combo is
// a single instrument scaled by ± {2, 1, 0.5} OR a pair with the same set of
// coefficients, whose vectors sum to the target's (§4.2).
//
// The target itself is never a valid source (§1.2 self-source exclusion) —
// `targetId` filters combos that include it.
const impliedForSideCombo = (legVec, comboIndex, side, sectionType, ctx, targetId) => {
    if (!comboIndex) return [];
    const key = vecKey(legVec);
    const cands = key ? comboIndex.recipes.get(key) : null;
    if (!cands || !cands.length) return [];
    // Keep the best raw per broker across every candidate combo, so each
    // broker still contributes one level even if their best recipe differs
    // from another broker's best.
    const bestByBkr = new Map();
    const better = side === 'bids' ? (a, b) => a > b : (a, b) => a < b;
    for (const combo of cands) {
        if (targetId && combo.some((e) => e.id === targetId)) continue;
        const priced = priceRecipe(combo, side, sectionType, ctx, comboIndex.books);
        if (!priced) continue;
        for (const [bkr, w] of priced) {
            const prev = bestByBkr.get(bkr);
            if (!prev || better(w.raw, prev.raw)) bestByBkr.set(bkr, w);
        }
    }
    return shapePerBroker(bestByBkr, sectionType, side);
};

// Pick the single best winner (across every broker) out of priceRecipe's
// Map<bkr, winner>. Used by legacy single-level paths (cross-curve
// enrichment, meeting-spread rows, custom rows).
const bestWinner = (winnersByBkr, side) => {
    if (!winnersByBkr || winnersByBkr.size === 0) return null;
    const better = side === 'bids' ? (a, b) => a > b : (a, b) => a < b;
    let best = null;
    for (const [, w] of winnersByBkr) {
        if (!best || better(w.raw, best.raw)) best = w;
    }
    return best;
};

// Shape every per-broker winner into a renderable level. Returns an array
// (possibly empty) — one entry per broker that can derive on this side.
const shapePerBroker = (winnersByBkr, sectionType, side) => {
    if (!winnersByBkr || winnersByBkr.size === 0) return [];
    const out = [];
    for (const [, w] of winnersByBkr) {
        const level = shapeLevel(w, sectionType, side);
        if (level) out.push(level);
    }
    return out;
};

// Pick the best derivation on one side across every path we support: native
// Path A, then Path B combos. Bids and offers pick independently, so a
// crossed implied is a possible (and correct) outcome (§7).
// Per broker: Path A + Path B merged, best of the two kept per broker.
// Returns an array — one entry per broker that can derive. Trader sees every
// entry; broker viewer already got filtered to their own company upstream in
// priceRecipe.
const impliedForSideByBroker = ({ legVec, outrightBooks, comboIndex, side, sectionType, ctx, targetId }) => {
    const better = side === 'bids' ? (a, b) => a > b : (a, b) => a < b;
    const pathA = outrightBooks ? impliedForSide(legVec, outrightBooks, side, sectionType, ctx) : [];
    const pathB = comboIndex ? impliedForSideCombo(legVec, comboIndex, side, sectionType, ctx, targetId) : [];
    const bestByBkr = new Map();
    [...pathA, ...pathB].forEach((lvl) => {
        const prev = bestByBkr.get(lvl.bkr);
        if (!prev || better(Number(lvl.price), Number(prev.price))) bestByBkr.set(lvl.bkr, lvl);
    });
    return [...bestByBkr.values()];
};

// Legacy single-best wrapper — the overall winner across every broker on this
// side. Secondary synthetic paths (meeting-spread rows, custom rows, basis
// enrichment) still use this because they emit one implied per row shape; the
// main `withImplied` + `buildSyntheticSpreadRow` paths use per-broker instead.
const impliedForSideBest = (args) => {
    const list = impliedForSideByBroker(args);
    if (!list.length) return null;
    const better = args.side === 'bids'
        ? (a, b) => Number(a.price) > Number(b.price)
        : (a, b) => Number(a.price) < Number(b.price);
    return list.reduce((best, l) => (!best || better(l, best) ? l : best), null);
};

// True iff an implied `impLevel` matches or beats the resting top on `side`.
// A tie is still worth showing: the resting and the implied are two separate
// pieces of liquidity at the same price, and hiding the implied on equality
// makes a viewer's own post appear to "eat" the implied. Only strictly-worse
// implieds get hidden.
const beatsResting = (impLevel, restingLevels, side, serverTime) => {
    const live = (restingLevels || []).filter((l) => isLivePrice(l, serverTime));
    if (!live.length) return true;
    const top = live.reduce((best, l) => {
        const p = Number(l.price);
        if (!Number.isFinite(p)) return best;
        if (best === null) return p;
        return side === 'bids' ? Math.max(best, p) : Math.min(best, p);
    }, null);
    if (top === null) return true;
    const derived = Number(impLevel.price);
    return side === 'bids' ? derived >= top : derived <= top;
};

// Meeting-date outrights and spreads share ONE section (`meetingDates`) — the
// store doesn't emit a separate `meetingDatesSpread` section, so per-row we
// distinguish a spread (has `start1Label`) from an outright. Every other
// spread/fly type lives in its own section, so the section type is enough.
// See app/resources/irs-clob/helpers/irs-clob-key.js (classifyBond).
const effectiveType = (row, sectionType) => {
    if (sectionType === 'meetingDates' && row?.start1Label) return 'meetingDatesSpread';
    return sectionType;
};

// Splice implied levels into a row's order book if a derivation is available
// AND beats the resting top. Returns a shallow copy; never mutates input.
export const withImplied = (row, sectionType, outrightBooksByType, ctx) => {
    // A non-domestic AUD price is its own instrument: it never receives an implied (nor feeds one).
    if (!row || row.nonDomestic) return row;
    const rowType = effectiveType(row, sectionType);
    const legVec = instrumentLegs(row, rowType);
    if (!legVec) return row;
    // Structure targets go through Path A + Path B. Outright targets skip
    // Path A (self-decomposition is trivial) but still run Path B — e.g.
    // outright X + spread X-Y → outright Y when Y is not yet quoted, or
    // when the combo derives a better price than the resting top.
    const isOutrightTarget = OUTRIGHT_LEG_TYPES.has(rowType);
    if (!IMPLIED_TARGET_TYPES.has(rowType) && !isOutrightTarget) return row;
    // Route the target to the correct outright family on the same curve.
    // A fwd spread MUST source from fwd outrights, never spot — silent
    // mis-routing would price the wrong instrument. Outright targets self-
    // route to their own family (spot → spot pool, fwd → fwd pool, …).
    const outrightFamily = SPREAD_TO_OUTRIGHT_TYPE[rowType] || rowType;
    const outrightBooks = !isOutrightTarget && outrightBooksByType && outrightBooksByType.get
        ? outrightBooksByType.get(outrightFamily)
        : null;
    // Path B (combo index) doesn't apply to custom — its pool is keyed by
    // plain tenor legs and custom rows use composite start|end keys.
    const comboIndex = !CUSTOM_TYPES.has(rowType) && ctx.combosByType && ctx.combosByType.get
        ? ctx.combosByType.get(outrightFamily)
        : null;
    if ((!outrightBooks || outrightBooks.size === 0) && !comboIndex) return row;

    const bids = [...(row.orderBook?.bids || [])];
    const offers = [...(row.orderBook?.offers || [])];
    const targetId = row.key || null;

    // Always surface a derived implied — display gating on "improves on
    // resting top" is out of the rulebook (§ Out of scope) and REQUIREMENT.md
    // is ambiguous on ties; the desk wants the implied visible whether or not
    // a resting price already sits at/beyond it, so `insertRanked` slots it in
    // at its correct price rank.
    const bidsImp = impliedForSideByBroker({ legVec, outrightBooks, comboIndex, side: 'bids', sectionType: rowType, ctx, targetId });
    bidsImp.forEach((b) => insertRanked(bids, b, 'bids'));
    const offersImp = impliedForSideByBroker({ legVec, outrightBooks, comboIndex, side: 'offers', sectionType: rowType, ctx, targetId });
    offersImp.forEach((o) => insertRanked(offers, o, 'offers'));

    if (bidsImp.length || offersImp.length) {
        return { ...row, orderBook: { ...row.orderBook, bids, offers } };
    }
    return row;
};

const insertRanked = (list, level, side) => {
    const p = Number(level.price);
    const ix = list.findIndex((l) => {
        const q = Number(l.price);
        if (!Number.isFinite(q)) return true;
        return side === 'bids' ? q < p : q > p;
    });
    if (ix < 0) list.push(level);
    else list.splice(ix, 0, level);
};

// Leg-key -> orderBook map for ONE outright section. Tenor sections key by
// `endLabel` (e.g. "5Y"); meeting-date sections key by `startLabel` (period
// name, e.g. "SEP26 RBA") since their `endLabel` is a period-end DATE that
// would collide between spread and outright lookups. Pass the section's
// structureType so the right key is used.
//
// Meeting-date outrights and spreads share ONE section (see effectiveType).
// Spread rows in that section carry the same `startLabel` as their near-leg
// outright, so we MUST skip them here — otherwise the spread's empty
// orderBook overwrites the outright's under the same map key.
export const buildOutrightBooks = (outrightRows, sectionType = 'outright') => {
    const out = new Map();
    (outrightRows || []).forEach((row) => {
        if (!row) return;
        // Skip spread rows that live inside a shared outright section
        // (only meetingDates today; extend this guard if another section
        // ever merges outrights and spreads).
        if (row.start1Label || row.nonDomestic) return;
        const key = legKey(row, sectionType);
        if (!key) return;
        out.set(key, row.orderBook || { bids: [], offers: [] });
    });
    return out;
};

// One-shot builder: from a curve group's full section list, return a
// Map<outrightSectionType, Map<tenor, orderBook>> ready to hand to
// `withImplied` (via impliedCtx.outrightBooksByType). Sections whose type is
// not a known outright variant are ignored.
const OUTRIGHT_TYPES = new Set(Object.values(SPREAD_TO_OUTRIGHT_TYPE));
export const buildOutrightBooksByType = (sections) => {
    const byType = new Map();
    (sections || []).forEach((section) => {
        if (!section || !OUTRIGHT_TYPES.has(section.structureType)) return;
        const rows = (section.rowOrder || []).map((k) => section.rows[k]);
        byType.set(section.structureType, buildOutrightBooks(rows, section.structureType));
    });
    return byType;
};

// ---------------------------------------------------------------------------
// Path B — combo indexer.
//
// For each outright section (spot, fwd, bob, sixs, meetingDates), gather every
// live-priced instrument in the same leg space: the outrights themselves plus
// any structures on that space that have a resting price. Index every single-
// instrument scale (± {2, 1, 0.5}) and every two-instrument combination of
// scales, keyed by the resulting leg vector.
//
// A target's Path B derivation is a hash lookup on its own leg vector against
// this index; matching recipes are priced and the best per side wins.
// Implieds themselves never enter the pool (§2 — implieds are never sources).
// ---------------------------------------------------------------------------

// The six coefficients from §4.2. Half-weights (± 0.5) are required for
// half-belly recipes like `0.5·fly + 0.5·2y10y → 2y×5y`. Removing them
// would make several genuine derivations unreachable.
const PATH_B_COEFS = [-2, -1, -0.5, 0.5, 1, 2];

// Which spread/fly section types share leg space with each outright section.
// Custom (diff-start-date) rows are user-authored composites with no clean
// Path B semantics — keep them out of the combo pool.
const SPREADS_FOR_OUTRIGHT = Object.entries(SPREAD_TO_OUTRIGHT_TYPE)
    .filter(([spread]) => !CUSTOM_TYPES.has(spread))
    .reduce((acc, [spread, outright]) => {
        if (!acc[outright]) acc[outright] = [];
        acc[outright].push(spread);
        return acc;
    }, {});

// One instrument in the combo pool. `id` matches the store row key (or
// synthesised for outrights via `t:<tenor>` when the outright section keys
// on legKey rather than key) so the self-source guard can filter it out.
const poolEntry = (row, sectionType, book) => {
    const legs = instrumentLegs(row, effectiveType(row, sectionType));
    if (!legs) return null;
    const id = row.key || `t:${legKey(row, sectionType)}`;
    // Meeting rows carry the period END DATE in `endLabel` and buildTenor joins
    // that as `row.tenor` (e.g. "04/11/26") — useless for the derivation panel.
    // Prefer the period label ("SEP26 RBA") for meeting sources; a meeting
    // spread reads as "near → far" so both legs are named.
    let label = row.tenor || legKey(row, sectionType) || row.startLabel || '';
    if (sectionType === 'meetingDates') {
        label = row.start1Label
            ? `${row.startLabel} → ${row.start1Label}`
            : (row.startLabel || label);
    }
    // Outright section rows quote in percent, spreads/flies in bp — flag
    // this so priceRecipe can convert to the target's unit correctly.
    const sourceIsOutright = OUTRIGHT_TYPES.has(sectionType)
        && !row.start1Label && !row.end1Label && !row.end2Label;
    return { id, label, legs, book, sourceIsOutright, sectionType };
};

const hasLiveLevel = (book, serverTime) => (
    (book?.bids || []).some((l) => isLivePrice(l, serverTime))
    || (book?.offers || []).some((l) => isLivePrice(l, serverTime))
);

// Build the combo pool for ONE outright section family. Includes the outright
// section rows AND the mapped spread/fly section rows, both filtered to
// live-priced instruments only (an unquoted leg cannot participate).
const buildPool = (outrightSection, spreadSections, ctx) => {
    const pool = [];
    if (outrightSection) {
        (outrightSection.rowOrder || []).forEach((k) => {
            const row = outrightSection.rows[k];
            if (!row || row.nonDomestic || !hasLiveLevel(row.orderBook, ctx.serverTime)) return;
            const entry = poolEntry(row, outrightSection.structureType, row.orderBook);
            if (entry) pool.push(entry);
        });
    }
    spreadSections.forEach((section) => {
        (section.rowOrder || []).forEach((k) => {
            const row = section.rows[k];
            if (!row || row.nonDomestic || !hasLiveLevel(row.orderBook, ctx.serverTime)) return;
            const entry = poolEntry(row, section.structureType, row.orderBook);
            if (entry) pool.push(entry);
        });
    });
    return pool;
};

// Turn a pool into a Map<vecKey, Recipe[]>. Single-instrument scales + all
// two-instrument pair combinations. A result is kept only in a shape a target
// can have (instrumentLegs): outright {1}, spread {-1,1}, fly {-1,2,-1} — any
// other vector is never looked up.
const MAX_TARGET_LEGS = 3;
const buildComboIndex = (pool) => {
    const index = new Map();
    // ca·wa + cb·wb over `legs` (sorted the way vecKey sorts them). The shape is
    // checked on plain numbers in a hot loop, so a rejected combo allocates nothing.
    const push = (legs, wa, ca, wb, cb, recipe) => {
        let count = 0;
        let ones = 0;
        let minusOnes = 0;
        let twos = 0;
        for (let n = 0; n < legs.length; n += 1) {
            const v = wa[n] * ca + (wb ? wb[n] * cb : 0);
            if (v > 1e-9 || v < -1e-9) {
                count += 1;
                if (v > 0.9999995 && v < 1.0000005) ones += 1;
                else if (v > -1.0000005 && v < -0.9999995) minusOnes += 1;
                else if (v > 1.9999995 && v < 2.0000005) twos += 1;
                else return;
            }
        }
        const isTarget = (count === 1 && ones === 1)
            || (count === 2 && ones === 1 && minusOnes === 1)
            || (count === MAX_TARGET_LEGS && minusOnes === 2 && twos === 1);
        if (!isTarget) return;
        const parts = [];
        for (let n = 0; n < legs.length; n += 1) {
            const v = wa[n] * ca + (wb ? wb[n] * cb : 0);
            if (Math.abs(v) > 1e-9) parts.push(`${legs[n]}:${Math.round(v)}`);
        }
        const k = parts.join(',');
        const list = index.get(k);
        if (list) list.push(recipe);
        else index.set(k, [recipe]);
    };
    // One entry per instrument and coefficient. Every entry preserves the
    // source's unit flag so priceRecipe can convert to the target's unit
    // correctly, and points at its pool slot (`ix`), not a book, so the index
    // can be reused while only prices move.
    const entries = pool.map((inst, ix) => PATH_B_COEFS.map((c) => ({
        id: inst.id,
        label: inst.label,
        ix,
        c,
        sourceIsOutright: inst.sourceIsOutright,
        sectionType: inst.sectionType
    })));
    const legNames = pool.map((inst) => Object.keys(inst.legs).sort());
    // Singles.
    pool.forEach((inst, i) => {
        const weights = legNames[i].map((leg) => inst.legs[leg]);
        entries[i].forEach((e) => push(legNames[i], weights, e.c, null, 0, [e]));
    });
    // Pairs (i, j) with i < j — avoid double-counting the same combo. Only
    // shared legs can cancel, so a pair that keeps more than three legs under
    // any coefficients is skipped.
    for (let i = 0; i < pool.length; i += 1) {
        for (let j = i + 1; j < pool.length; j += 1) {
            const union = [...new Set([...legNames[i], ...legNames[j]])].sort();
            const shared = legNames[i].length + legNames[j].length - union.length;
            if (union.length - shared <= MAX_TARGET_LEGS) {
                const wi = union.map((leg) => pool[i].legs[leg] || 0);
                const wj = union.map((leg) => pool[j].legs[leg] || 0);
                entries[i].forEach((a) => entries[j].forEach((b) => push(union, wi, a.c, wj, b.c, [a, b])));
            }
        }
    }
    return index;
};

// The index depends only on which instruments are live, not on their prices,
// so a price-only update reuses it. Shared across curves: an identical pool
// signature means an identical topology.
const COMBO_CACHE_MAX = 64;
const comboIndexCache = new Map();
const cachedComboIndex = (pool) => {
    const signature = pool
        .map((inst) => `${inst.id}|${inst.label}|${vecKey(inst.legs)}|${inst.sourceIsOutright ? 1 : 0}|${inst.sectionType}`)
        .join('\n');
    let recipes = comboIndexCache.get(signature);
    if (recipes) comboIndexCache.delete(signature);
    else recipes = buildComboIndex(pool);
    comboIndexCache.set(signature, recipes);
    if (comboIndexCache.size > COMBO_CACHE_MAX) comboIndexCache.delete(comboIndexCache.keys().next().value);
    return recipes;
};

// One-shot public builder: from a curve group's full section list, return a
// Map<outrightSectionType, comboIndex> ready to hand to `withImplied` /
// `generateImpliedRows` via `impliedCtx.combosByType`.
export const buildImpliedCombosByType = (sections, ctx = {}) => {
    const byType = new Map();
    const outrightByType = new Map();
    const spreadsByOutrightType = new Map();
    (sections || []).forEach((section) => {
        if (!section) return;
        const t = section.structureType;
        if (OUTRIGHT_TYPES.has(t)) {
            outrightByType.set(t, section);
        } else if (SPREAD_FLY_TYPES.has(t)) {
            const ot = SPREAD_TO_OUTRIGHT_TYPE[t];
            const list = spreadsByOutrightType.get(ot) || [];
            list.push(section);
            spreadsByOutrightType.set(ot, list);
        }
    });
    OUTRIGHT_TYPES.forEach((ot) => {
        const outrightSection = outrightByType.get(ot);
        const spreadSections = spreadsByOutrightType.get(ot) || [];
        if (!outrightSection && !spreadSections.length) return;
        const pool = buildPool(outrightSection, spreadSections, ctx);
        if (pool.length < 1) return;
        byType.set(ot, { recipes: cachedComboIndex(pool), books: pool.map((inst) => inst.book) });
    });
    return byType;
};

// Patch a meeting-spread row's missing `end1` by looking up the far period's
// outright `end`. Backend seed ships meeting-spread rows with `end1: null`,
// which the plot URL builder would encode as "NaNy" — but the answer is
// already in the store: the far period's outright row carries its own `end`
// (the date of the meeting AFTER it), which IS the spread's `end1`. Same fix
// applies to synthetic meeting spreads for consistency.
export const patchMeetingSpreadEnd1 = (row, outrightRowsByType) => {
    if (!row || !row.start1Label || row.end1) return row;
    const rows = outrightRowsByType?.get?.('meetingDates');
    if (!rows) return row;
    const farOutright = rows.get(norm(row.start1Label));
    const end1 = farOutright?.end;
    if (!end1) return row;
    return { ...row, end1 };
};

// Companion to buildOutrightBooksByType: keep the source rows themselves, so
// synthetic implied rows (Phase 3.5) can inherit curve identity — irsId,
// currency, bob/sixs curve ids, min sizes — from a real sibling. Without
// these fields the plot URL builder opens the wrong page and the inline
// price form crashes on missing labels. See docs/requirements/clob-implied-pricing.
export const buildOutrightRowsByType = (sections) => {
    const byType = new Map();
    (sections || []).forEach((section) => {
        if (!section || !OUTRIGHT_TYPES.has(section.structureType)) return;
        const rowsByKey = new Map();
        (section.rowOrder || []).forEach((k) => {
            const row = section.rows[k];
            if (!row || row.start1Label || row.nonDomestic) return; // skip spreads sharing an outright section, and ND rows
            const key = legKey(row, section.structureType);
            if (key) rowsByKey.set(key, row);
        });
        byType.set(section.structureType, rowsByKey);
    });
    return byType;
};

// BOB / 6s3s outright System mids are already quoted in bp; plain outrights in %.
const BP_OUTRIGHT_TYPES = new Set(['bob', 'sixs']);

// System mid of a synthetic implied spread / fly (it has no backend row to carry one): its
// outright legs' System mids, combined with the leg weights the implied pricing uses — far − near
// for a spread, 2·belly − near − far for a fly — in bp, at the grid's 3 dp. Null when a leg has none.
export const impliedSystemMid = (row, sectionType, outrightRowsByType, outrightsInBp = false) => {
    const rowType = effectiveType(row, sectionType);
    const family = SPREAD_TO_OUTRIGHT_TYPE[rowType];
    const legVec = family ? instrumentLegs(row, rowType) : null;
    const legRows = outrightRowsByType?.get?.(family);
    if (!legVec || !legRows) return null;
    const legs = Object.entries(legVec).map(([leg, weight]) => [legRows.get(leg)?.systemMid, weight]);
    if (legs.some(([mid]) => mid == null || mid === '' || !Number.isFinite(Number(mid)))) return null;
    const sum = legs.reduce((total, [mid, weight]) => total + weight * Number(mid), 0);
    const inBp = outrightsInBp || BP_OUTRIGHT_TYPES.has(family);
    return (inBp ? sum : sum * 100).toFixed(3);
};

// One template row per outright section — the first row with an irsId is
// enough. Every synthetic row on that leg space stamps the same
// curve-identity fields onto itself from this template.
export const buildCurveTemplateByType = (sections) => {
    const byType = new Map();
    (sections || []).forEach((section) => {
        if (!section || !OUTRIGHT_TYPES.has(section.structureType)) return;
        const first = (section.rowOrder || [])
            .map((k) => section.rows[k])
            .find((r) => r && r.irsId);
        if (first) byType.set(section.structureType, first);
    });
    return byType;
};

// ---------------------------------------------------------------------------
// Row generation — surface implieds for structures that have no store row yet.
//
// The store only carries structures a broker has ever quoted (or the backend
// seeded as standard). A viable implied for a structure nobody has entered
// would otherwise vanish. `generateImpliedRows` enumerates every candidate
// leg tuple, prices it via `impliedForSide`, and returns synthetic rows for
// the ones that actually derive a price on at least one side.
//
// Section coverage: every spread/fly section this phase routes plus the
// meeting-dates section (which mixes outrights and consecutive-period
// spreads — handled by generating spreads only). Outright sections do not
// generate; a missing outright is not derivable in Path A.
// ---------------------------------------------------------------------------

// Signature for dedup vs the section's existing rows. Order-independent:
// tenors sorted by years, joined into one string.
const legSig = (legVec) => Object.keys(legVec)
    .filter((k) => Math.abs(legVec[k]) > 1e-9)
    .sort((a, b) => yrs(a) - yrs(b))
    .map((k) => `${k}:${legVec[k]}`)
    .join('|');

const existingLegSigs = (section, sectionType) => {
    const sigs = new Set();
    (section?.rowOrder || []).forEach((k) => {
        const row = section.rows[k];
        if (row?.nonDomestic) return;
        const rowType = effectiveType(row, sectionType);
        const legs = instrumentLegs(row, rowType);
        if (legs) sigs.add(legSig(legs));
    });
    return sigs;
};

// Match a leg tuple against the section's curated `coreSpreads` / `coreFlies`
// (set membership on the sorted-tenor tuple). Rows off the list are marked
// non-standard so section filters can hide them by default.
const standardSigs = new WeakMap();
const toSigSet = (tuples) => tuples && new Set(tuples.map((tuple) => tuple.map((t) => norm(t)).sort((a, b) => yrs(a) - yrs(b)).join('|')));
const isStandardLegs = (section, tenorsSorted) => {
    const list = section?.standardList;
    if (!list) return true;
    if (!standardSigs.has(list)) standardSigs.set(list, { spreads: toSigSet(list.coreSpreads), flies: toSigSet(list.coreFlies) });
    const sigs = standardSigs.get(list)[tenorsSorted.length === 3 ? 'flies' : 'spreads'];
    return Boolean(sigs?.has(tenorsSorted.map((t) => norm(t)).join('|')));
};

// Backend BOND_TYPE_ENUM value for a synthetic spread/fly, per section
// variant. Spot (spreadFly) and fwd (fwdSpreadFly) leave `type` null —
// buildRowMeta on the backend does the same for plain spread/butterfly
// rows and irsStructureFields infers from leg presence.
const IMP_BOND_TYPES = {
    bobSpreadFly: { spread: 'bobSpread', fly: 'bobButterfly' },
    sixsSpreadFly: { spread: '6s3sSpread', fly: '6s3sButterfly' },
    audDcSpreadFly: { spread: 'audMixMatchSpread', fly: 'audMixMatchButterfly' }
};
const CUSTOM_BOND_TYPE = {
    custom: 'spreadDiffStartDates',
    bobCustom: 'bobSpreadDiffStartDates',
    sixsCustom: '6s3spreadDiffStartDates'
};
const bondTypeFor = (sectionType, legCount) => {
    const map = IMP_BOND_TYPES[sectionType];
    if (!map) return null;
    return legCount === 3 ? map.fly : map.spread;
};

// Copy curve-identity fields off a template row so the plot URL builder and
// the price-entry form both find what they read. Everything the client-side
// action flow needs — irsId, currency, bob/sixs curve ids, min sizes,
// startLabel/start_date, imm — comes from an existing outright on the same
// curve. Never overrides fields the synthetic row already carries (legs,
// key, orderBook, implied flag).
const stampCurveIdentity = (row, template) => {
    if (!template) return row;
    return {
        irsId: template.irsId ?? null,
        currency: template.currency ?? null,
        bobCurveId: template.bobCurveId ?? null,
        sixsCurveId: template.sixsCurveId ?? null,
        mos: template.mos ?? null,
        mrs: template.mrs ?? null,
        startLabel: row.startLabel ?? template.startLabel ?? 'Spot',
        start_date: row.start_date ?? template.start_date ?? template.start ?? null,
        imm: template.imm ?? null,
        immLabel: template.immLabel ?? null,
        ...row
    };
};

// Build one synthetic row (returns null if it fails to price both sides).
// `tenors` is [near, far] or [near, belly, far], sorted by years ascending.
// `sourceRows` is Map<tenor, outrightRow> — the near/mid/far outrights on
// the same leg space, used to inherit end-leg DATES so the entry form
// doesn't downgrade the row to an outright on `raw.end1 ? ... : null`.
const buildSyntheticSpreadRow = ({ tenors, sectionType, outrightBooks, comboIndex, sourceRows, ctx, start, standard, template }) => {
    const legVec = tenors.length === 2
        ? { [tenors[0]]: -1, [tenors[1]]: 1 }
        : { [tenors[0]]: -1, [tenors[1]]: 2, [tenors[2]]: -1 };
    // Path A native + Path B combos, per broker, picked independently per side.
    const bidsImp = impliedForSideByBroker({ legVec, outrightBooks, comboIndex, side: 'bids', sectionType, ctx });
    const offersImp = impliedForSideByBroker({ legVec, outrightBooks, comboIndex, side: 'offers', sectionType, ctx });
    // Standard (curated) structures stay in the grid even when no side derives
    // — the desk expects to see the row and post its own price. Non-standard
    // rows are only surfaced by an active implied, so they vanish with it.
    if (!bidsImp.length && !offersImp.length && !standard) return null;
    const tenorLabel = tenors.map((t) => t.toLowerCase()).join(' x ');
    const bids = [...bidsImp].sort((a, b) => Number(b.price) - Number(a.price));
    const offers = [...offersImp].sort((a, b) => Number(a.price) - Number(b.price));
    const key = `implied|${sectionType}|${legSig(legVec)}`;
    // Leg dates: prefer the source outright's `end` DATE; fall back to the
    // tenor label so `raw.end1 ? ...` truth-checks pass and the form treats
    // this as a spread/fly rather than downgrading to outright.
    const legDate = (t) => sourceRows?.get(t)?.end ?? sourceRows?.get(t)?.endLabel ?? spacedTenor(t);
    // Store rows carry tenors as "6 M" / "1 Y" (space between number and unit) —
    // several downstream paths (SpreadTab.getEndDate, form validation, plot URL
    // custom-labels) `split(' ')` on the label and error out when the unit is
    // missing. Emit the same spaced form as real rows.
    const spacedTenors = tenors.map(spacedTenor);
    const type = bondTypeFor(sectionType, tenors.length);
    const base = tenors.length === 2 ? {
        key,
        start,
        tenor: tenorLabel,
        end1Label: spacedTenors[0],
        endLabel: spacedTenors[1],
        end1: legDate(tenors[0]),
        end: legDate(tenors[1]),
        end2: null,
        end2Label: null,
        orderBook: { bids, offers },
        midMatch: [],
        standard,
        implied: true,
        isLead: true,
        structureType: sectionType,
        ...(type ? { type } : {})
    } : {
        key,
        start,
        tenor: tenorLabel,
        end1Label: spacedTenors[0],
        end2Label: spacedTenors[1],
        endLabel: spacedTenors[2],
        end1: legDate(tenors[0]),
        end2: legDate(tenors[1]),
        end: legDate(tenors[2]),
        orderBook: { bids, offers },
        midMatch: [],
        standard,
        implied: true,
        isLead: true,
        structureType: sectionType,
        ...(type ? { type } : {})
    };
    return stampCurveIdentity(base, template);
};

// A meeting-date spread candidate is between two consecutive meeting periods,
// per prototype — non-consecutive combinations are not conventionally quoted.
const buildSyntheticMeetingSpreadRow = ({ near, far, outrightBooks, comboIndex, ctx, standard }) => {
    const legVec = { [norm(near.startLabel)]: -1, [norm(far.startLabel)]: 1 };
    const bid = impliedForSideBest({ legVec, outrightBooks, comboIndex, side: 'bids', sectionType: 'meetingDatesSpread', ctx });
    const offer = impliedForSideBest({ legVec, outrightBooks, comboIndex, side: 'offers', sectionType: 'meetingDatesSpread', ctx });
    // Consecutive meeting spreads are the section's "standard" list — keep
    // the row even without a live implied so the desk can post their own.
    if (!bid && !offer && !standard) return null;
    const base = {
        key: `implied|meetingDatesSpread|${legSig(legVec)}`,
        start: near.start,
        tenor: `${near.startLabel} x ${far.startLabel}`,
        // Mirror backend buildRowMeta (irs-clob-key.js): the meeting Structure
        // column reads `raw.structureLabel`. Without it, structureLabel() in
        // IrsOrderBookGrid falls back to `row.start` — the near-leg DATE —
        // which reads as "just the first period name" on synthetic rows.
        structureLabel: `${near.startLabel} → ${far.startLabel}`,
        startLabel: near.startLabel,
        start1Label: far.startLabel,
        // `near.start` is buildRowMeta's startIdentity DISPLAY string ("Nov 26
        // RBA"), not a date — reading it here would ship the label through the
        // createIrsPrice payload's start.value, which the backend validator
        // rejects. The real ISO date is on `.start_date`.
        start_date: near.start_date ?? null,
        start1: far.start_date ?? null,
        // Mirror the backend-scaffolded meeting-spread row shape
        // (scaffoldMeetingSpreadBond in irs-clob-shape.js): the near leg carries
        // end + endLabel (period end as DD/MM/yy), and the far leg lives only on
        // start1 / start1Label. This keeps the sort key (compareRows reads end1
        // / end) identical to the real row so dedupe lines them up.
        end: near.end ?? null,
        end1: null,
        endLabel: near.endLabel ?? null,
        end1Label: null,
        end2Label: null,
        orderBook: { bids: bid ? [bid] : [], offers: offer ? [offer] : [] },
        midMatch: [],
        standard,
        implied: true,
        isLead: true,
        structureType: 'meetingDates',
        type: 'meetingDates',
        mdType: near.mdType ?? null
    };
    // Meeting rows carry irsId themselves — inherit off the near period.
    return stampCurveIdentity(base, near);
};

// Total maturity in years for a fwd outright row — used to order the two
// legs of a synthetic custom row so leg1 is the shorter-dated (matches the
// `buildSpreadDiffStartDateLegs` convention in ui/lib/constants.js).
const legMaturity = (row) => yrs(row?.startLabel) + yrs(row?.endLabel);

// One synthetic custom row from two live fwd-outright legs. leg1 is the
// shorter-maturity, at coefficient -1; leg2 the longer, at +1.
const buildSyntheticCustomRow = ({ legA, legB, sectionType, ctx, template }) => {
    const [leg1, leg2] = legMaturity(legA) <= legMaturity(legB) ? [legA, legB] : [legB, legA];
    const k1 = `${norm(leg1.startLabel)}|${norm(leg1.endLabel)}`;
    const k2 = `${norm(leg2.startLabel)}|${norm(leg2.endLabel)}`;
    if (!k1 || !k2 || k1 === k2) return null;
    const legVec = { [k1]: -1, [k2]: 1 };
    const outrightBooks = new Map([
        [k1, leg1.orderBook || { bids: [], offers: [] }],
        [k2, leg2.orderBook || { bids: [], offers: [] }]
    ]);
    const bid = impliedForSideBest({ legVec, outrightBooks, comboIndex: null, side: 'bids', sectionType, ctx });
    const offer = impliedForSideBest({ legVec, outrightBooks, comboIndex: null, side: 'offers', sectionType, ctx });
    if (!bid && !offer) return null;
    const type = CUSTOM_BOND_TYPE[sectionType] || null;
    // Fwd outright rows carry BOTH `start` (the IMM label, e.g. "Z26") and
    // `start_date` (the ISO date). The backend's create-irs-price parses
    // start.value / start1 as tenures, so both must be ISO — pick the first
    // ISO-shaped value we see and fall back to the label only if there's
    // nothing else.
    const pickIso = (...values) => values.find((v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) || null;
    const leg1Iso = pickIso(leg1.start_date, leg1.start);
    const leg2Iso = pickIso(leg2.start_date, leg2.start);
    const base = {
        key: `implied|${sectionType}|${legSig(legVec)}`,
        start: leg1.start ?? leg1.start_date ?? null,
        tenor: `${leg1.startLabel}-${leg1.endLabel} X ${leg2.startLabel}-${leg2.endLabel}`,
        startLabel: leg1.startLabel,
        endLabel: leg1.endLabel,
        start1Label: leg2.startLabel,
        end1Label: leg2.endLabel,
        start_date: leg1Iso,
        start1: leg2Iso,
        end: leg1.end ?? null,
        end1: leg2.end ?? null,
        end2: null,
        end2Label: null,
        orderBook: { bids: bid ? [bid] : [], offers: offer ? [offer] : [] },
        midMatch: [],
        standard: false,
        implied: true,
        isLead: true,
        structureType: sectionType,
        ...(type ? { type } : {})
    };
    return stampCurveIdentity(base, template);
};

// Public entry: return the synthetic rows this section should surface, given
// the curve group's outright books. Empty array when nothing can be added.
export const generateImpliedRows = (section, outrightBooksByType, ctx) => {
    if (!section || !outrightBooksByType) return [];
    const type = section.structureType;

    // Meeting section: mixed outrights + consecutive-period spreads.
    if (type === 'meetingDates') {
        const outrightBooks = outrightBooksByType.get('meetingDates');
        if (!outrightBooks || outrightBooks.size === 0) return [];
        const periods = (section.rowOrder || [])
            .map((k) => section.rows[k])
            // Stub/Full Dt variants trade standalone only — never source
            // them into meeting-spread implieds, and skip the near === far
            // self-pair the same-period variants would otherwise produce.
            .filter((r) => r && r.startLabel && !r.start1Label && !r.mdType);
        if (periods.length < 2) return [];
        const existing = existingLegSigs(section, type);
        const out = [];
        // Enumerate every ordered pair of periods (i < j). Consecutive pairs
        // (j === i+1) are the curated "standard" quote list; non-consecutive
        // pairs are still valid quotes, marked non-standard so the Standard-
        // only section filter keeps hiding them by default.
        for (let i = 0; i < periods.length; i += 1) {
            for (let j = i + 1; j < periods.length; j += 1) {
                const near = periods[i];
                const far = periods[j];
                // A period without an `end` DATE has no next meeting to
                // compute the spread's own `end1` from — the plot URL would
                // encode "NaNy" and SpreadTab.addTermToDate2 would crash.
                if (!near.end || !far.end) continue;
                const sig = legSig({ [norm(near.startLabel)]: -1, [norm(far.startLabel)]: 1 });
                if (existing.has(sig)) continue;
                const row = buildSyntheticMeetingSpreadRow({
                    near,
                    far,
                    outrightBooks,
                    comboIndex: ctx?.combosByType?.get?.('meetingDates') || null,
                    ctx,
                    standard: j === i + 1
                });
                if (row) out.push(row);
            }
        }
        return out;
    }

    // Tenor spread / fly section: sourced from its mapped outright section.
    if (!SPREAD_FLY_TYPES.has(type)) return [];

    // Custom (diff-start-date): enumerate every pair of live fwd outrights on
    // the mapped fwd variant, surface each as one synthetic custom row.
    // Match-only against composite start|end keys — no forward-rate math.
    if (CUSTOM_TYPES.has(type)) {
        const fwdType = SPREAD_TO_OUTRIGHT_TYPE[type];
        const fwdRows = ctx?.outrightRowsByType?.get?.(fwdType);
        if (!fwdRows || fwdRows.size === 0) return [];
        const template = ctx?.curveTemplateByType?.get?.(fwdType) || null;
        const live = [...fwdRows.values()].filter((r) => r && hasLiveLevel(r.orderBook, ctx.serverTime));
        if (live.length < 2) return [];
        const existing = existingLegSigs(section, type);
        const out = [];
        for (let a = 0; a < live.length; a += 1) {
            for (let b = a + 1; b < live.length; b += 1) {
                const kA = `${norm(live[a].startLabel)}|${norm(live[a].endLabel)}`;
                const kB = `${norm(live[b].startLabel)}|${norm(live[b].endLabel)}`;
                const [k1, k2] = legMaturity(live[a]) <= legMaturity(live[b]) ? [kA, kB] : [kB, kA];
                const sig = legSig({ [k1]: -1, [k2]: 1 });
                if (existing.has(sig)) continue;
                const row = buildSyntheticCustomRow({ legA: live[a], legB: live[b], sectionType: type, ctx, template });
                if (row) out.push(row);
            }
        }
        return out;
    }

    // ponytail: fwdSpreadFly synthetic enumeration still assumes a single-
    // start leg space. Skip until per-start enumeration is added — real
    // store-emitted fwd spread rows still imply correctly via `withImplied`.
    if (type === 'fwdSpreadFly') return [];
    const outrightType = SPREAD_TO_OUTRIGHT_TYPE[type];
    const outrightBooks = outrightBooksByType.get(outrightType);
    const comboIndex = ctx?.combosByType?.get?.(outrightType) || null;
    if ((!outrightBooks || outrightBooks.size === 0) && !comboIndex) return [];
    const sourceRows = ctx?.outrightRowsByType?.get?.(outrightType) || null;
    const template = ctx?.curveTemplateByType?.get?.(outrightType) || null;

    // Enumerate the tenor pool from every live source we have. Outrights
    // (Path A) contribute directly; live spread/fly rows in the same section
    // contribute their leg tenors so Path B combos can reach targets no
    // outright covers. Same-tenor duplicates deduped by Set.
    const tenorPool = new Set();
    if (outrightBooks) {
        outrightBooks.forEach((book, tenor) => {
            if ((book.bids || []).some((l) => isLivePrice(l, ctx.serverTime))
                || (book.offers || []).some((l) => isLivePrice(l, ctx.serverTime))) {
                tenorPool.add(tenor);
            }
        });
    }
    (section.rowOrder || []).forEach((k) => {
        const row = section.rows[k];
        if (!row || !hasLiveLevel(row.orderBook, ctx.serverTime)) return;
        [row.end1Label, row.end2Label, row.endLabel].forEach((t) => {
            const n = norm(t);
            if (n) tenorPool.add(n);
        });
    });
    const pool = [...tenorPool].sort((a, b) => yrs(a) - yrs(b));
    if (pool.length < 2) return [];

    const existing = existingLegSigs(section, type);
    // Fall back to the outright template's start when the section itself has
    // no rows (synthesised spread/fly section — no store row to read from).
    const start = (section.rowOrder || []).map((k) => section.rows[k]?.start).find(Boolean)
        || template?.start
        || template?.startLabel
        || '';
    const out = [];

    // Spreads: C(n, 2).
    for (let a = 0; a < pool.length; a += 1) {
        for (let b = a + 1; b < pool.length; b += 1) {
            const tenors = [pool[a], pool[b]];
            const sig = legSig({ [tenors[0]]: -1, [tenors[1]]: 1 });
            if (existing.has(sig)) continue;
            const row = buildSyntheticSpreadRow({
                tenors,
                sectionType: type,
                outrightBooks,
                comboIndex,
                sourceRows,
                ctx,
                start,
                standard: isStandardLegs(section, tenors),
                template
            });
            if (row) out.push(row);
        }
    }
    // Flies: C(n, 3). Meeting spreads are two-leg only; other sections do both.
    if (type !== 'meetingDatesSpread') {
        for (let a = 0; a < pool.length; a += 1) {
            for (let b = a + 1; b < pool.length; b += 1) {
                for (let c = b + 1; c < pool.length; c += 1) {
                    const tenors = [pool[a], pool[b], pool[c]];
                    const sig = legSig({ [tenors[0]]: -1, [tenors[1]]: 2, [tenors[2]]: -1 });
                    if (existing.has(sig)) continue;
                    const row = buildSyntheticSpreadRow({
                        tenors,
                        sectionType: type,
                        outrightBooks,
                        comboIndex,
                        sourceRows,
                        ctx,
                        start,
                        standard: isStandardLegs(section, tenors),
                        template
                    });
                    if (row) out.push(row);
                }
            }
        }
    }
    return out;
};

// Per-leg execution payload for an atomic hit/lift of an implied level.
// Each source leg's total fill = |weight| × parent size (`leg.size` on the
// engine record is the source's advertised capacity, NOT its at-parent-size
// consumption). That total is split FIFO across the leg's aggregated
// `bondPrices[]` — the real `bond_prices` rows behind the top-of-book
// aggregate (§2.3). Returns a FLAT list of `{ id, size }` the backend loops
// over, or `null` if the input is malformed.
export const impliedExecutionLegs = (legs, parentSize) => {
    const parent = Number(parentSize);
    if (!Array.isArray(legs) || !legs.length || !(parent > 0)) return null;
    const out = [];
    for (const l of legs) {
        let remaining = Math.abs(Number(l.weight)) * parent;
        if (!(remaining > 0)) return null;
        const pool = Array.isArray(l.bondPrices) && l.bondPrices.length
            ? l.bondPrices
            // Back-compat: pre-Phase-4 legs carried the ROW key as `id` and no
            // pool. If someone still passes that, we can't execute — bail.
            : null;
        if (!pool) return null;
        for (const bp of pool) {
            if (remaining <= 0) break;
            if (!bp?.id || !(Number(bp.size) > 0)) return null;
            const take = Math.min(remaining, Number(bp.size));
            out.push({ id: bp.id, size: take });
            remaining -= take;
        }
        // Not enough advertised size to cover the parent share = the top-of-book
        // moved since the level was rendered. Refuse rather than under-fill.
        if (remaining > 1e-9) return null;
    }
    return out;
};

// ---------------------------------------------------------------------------
// Custom / derived curve groups (THB ND/D basis).
//
// A basis instrument's leg vector spans two leg spaces: basis = primary -
// secondary (e.g. THB ND/D = OIS - NDOIS). Backend already ships this mapping
// via CUSTOM_IRS_CURVES for the trade-ticket flow; here we mirror the tickers
// on the client so the CLOB can synthesise a basis outright section from live
// primary + secondary books.
//
// Only surfaces when the trader has all three curves selected — a partial
// selection means the basis panel is off, per user request.
// ---------------------------------------------------------------------------
const CUSTOM_CURVE_GROUP_CFG = [
    { basisTicker: 'THBNDD', basisCurve: 'NDD', primaryTicker: 'TBSWHO', primaryCurve: 'D', secondaryTicker: 'TBSWH', secondaryCurve: 'ND' }
];

// Basis-curve IRS ids on the selected set — used by the CLOB to flag
// `outrightsInBp` for these groups so priceRecipe skips the 100x scaling that
// only applies when outrights are in percent.
export const getBasisIrsIds = (allIrs) => {
    const tickers = new Set(CUSTOM_CURVE_GROUP_CFG.map((c) => c.basisTicker));
    return new Set((allIrs || []).filter((i) => tickers.has(i?.ticker)).map((i) => i.id));
};

// Every curve a basis group joins (the basis and both its siblings) — the curves whose
// order books enrichBasisCurveGroups reads and whose sections it may replace.
export const getBasisGroupIrsIds = (allIrs) => {
    const tickers = new Set(CUSTOM_CURVE_GROUP_CFG.flatMap((c) => [c.basisTicker, c.primaryTicker, c.secondaryTicker]));
    return new Set((allIrs || []).filter((i) => tickers.has(i?.ticker)).map((i) => i.id));
};

// Row-key extractor. Spot outrights key on `endLabel` (e.g. "5Y"); fwd
// outrights key on composite `startLabel|endLabel` (e.g. "U6|5Y") so two
// starts on the same tenor don't collide. Same rule the main engine uses
// (see legKey / COMPOSITE_KEY_TYPES).
const outrightRowKey = (row, family) => {
    if (family === 'fwd') {
        const s = norm(row?.startLabel);
        const e = norm(row?.endLabel);
        return s && e ? `${s}|${e}` : null;
    }
    return norm(row?.endLabel);
};

// Build the merged nd:/d: outright book Map for one basis config, keyed by
// `family` (spot 'outright' → tenor; fwd 'fwd' → START|END composite).
// Shared by row-decoration and row-fabrication so leg keys stay identical.
const buildBasisContext = ({ primarySection, secondarySection, family = 'outright' }) => {
    if (!primarySection || !secondarySection) return null;
    const primaryByKey = new Map();
    const secondaryByKey = new Map();
    const outrightBooks = new Map();
    (primarySection.rowOrder || []).forEach((k) => {
        const r = primarySection.rows[k];
        if (!r || r.start1Label) return;
        const key = outrightRowKey(r, family);
        if (!key) return;
        primaryByKey.set(key, r);
        outrightBooks.set(`d:${key}`, r.orderBook || { bids: [], offers: [] });
    });
    (secondarySection.rowOrder || []).forEach((k) => {
        const r = secondarySection.rows[k];
        if (!r || r.start1Label) return;
        const key = outrightRowKey(r, family);
        if (!key) return;
        secondaryByKey.set(key, r);
        outrightBooks.set(`nd:${key}`, r.orderBook || { bids: [], offers: [] });
    });
    return { primaryByKey, secondaryByKey, outrightBooks, family };
};

// Expand a per-tenor basis coefficient map into a leg vector spanning both
// sibling curves. basis(tenor) = d(tenor) - nd(tenor), so a basis instrument
// carrying `{ tenor: c }` legs contributes `{ d:tenor: +c, nd:tenor: -c }`.
// Outright:  { t: +1 }        → { d:t:+1, nd:t:-1 }
// Spread:    { near:-1, far:+1 } → { d:near:-1, nd:near:+1, d:far:+1, nd:far:-1 }
// Butterfly: { near:-1, belly:+2, far:-1 } → six-entry vector.
const basisLegVec = (byTenor) => {
    const vec = {};
    Object.entries(byTenor).forEach(([t, c]) => {
        if (!t || !c) return;
        vec[`d:${t}`] = c;
        vec[`nd:${t}`] = -c;
    });
    return vec;
};

const impliedBasisSides = (legVec, outrightBooks, ctx) => {
    // Any sectionType not in OUTRIGHT_LEG_TYPES makes priceRecipe treat the
    // target as bp (§3.3, u=0.01) — basis and every basis spread/fly are bp.
    const bid = impliedForSideBest({ legVec, outrightBooks, comboIndex: null, side: 'bids', sectionType: 'basisImplied', ctx });
    const offer = impliedForSideBest({ legVec, outrightBooks, comboIndex: null, side: 'offers', sectionType: 'basisImplied', ctx });
    return { bid, offer };
};

const impliedBasisOutrightLevels = (tenor, outrightBooks, ctx) => (
    impliedBasisSides(basisLegVec({ [tenor]: 1 }), outrightBooks, ctx)
);

// Read a spread/fly row's legs (near/belly/far tenors + coefficients) exactly
// the way `instrumentLegs` reads a tenor spread/fly, so a basis spread and a
// same-shape rate spread pull their tenors off the same fields.
const readSpreadFlyTenors = (row) => {
    const legs = sortLegs([row.end1Label, row.end2Label, row.endLabel]);
    if (legs.length === 2) return { [legs[0]]: -1, [legs[1]]: 1 };
    if (legs.length === 3) return { [legs[0]]: -1, [legs[1]]: 2, [legs[2]]: -1 };
    return null;
};

// Spread/fly row tenor reader for FWD basis rows. Fwd spread legs share the
// row's IMM start (row.startLabel), so leg keys are composite `start|leg`
// per COMPOSITE_KEY_TYPES. Mirrors what instrumentLegs does for fwdSpreadFly.
const readFwdSpreadFlyLegs = (row) => {
    const s = norm(row?.startLabel);
    if (!s) return null;
    const legs = sortLegs([row.end1Label, row.end2Label, row.endLabel]);
    if (legs.length === 2) return { [`${s}|${legs[0]}`]: -1, [`${s}|${legs[1]}`]: 1 };
    if (legs.length === 3) return { [`${s}|${legs[0]}`]: -1, [`${s}|${legs[1]}`]: 2, [`${s}|${legs[2]}`]: -1 };
    return null;
};

// Enrich one basis outright section (spot 'outright' or fwd 'fwd'). Decorates
// existing rows with implied levels; fabricates rows for common keys the
// section didn't carry.
const enrichBasisOutrightSection = ({ basisSection, primarySection, secondarySection, family, basisIrs, ctx }) => {
    const bctx = buildBasisContext({ primarySection, secondarySection, family });
    if (!bctx) return null;
    const { primaryByKey, secondaryByKey, outrightBooks } = bctx;
    const commonKeys = new Set([...primaryByKey.keys()].filter((k) => secondaryByKey.has(k)));
    if (!commonKeys.size) return null;

    const existingKeys = new Set();
    const enrichedRows = { ...basisSection.rows };
    let changed = false;
    basisSection.rowOrder.forEach((rk) => {
        const row = basisSection.rows[rk];
        if (!row || row.start1Label) return;
        const key = outrightRowKey(row, family);
        if (!key) return;
        existingKeys.add(key);
        if (!commonKeys.has(key)) return;
        const legVec = basisLegVec({ [key]: 1 });
        const { bid, offer } = impliedBasisSides(legVec, outrightBooks, ctx);
        if (!bid && !offer) return;
        enrichedRows[rk] = {
            ...row,
            orderBook: {
                ...row.orderBook,
                bids: [...(row.orderBook?.bids || []), ...(bid ? [bid] : [])],
                offers: [...(row.orderBook?.offers || []), ...(offer ? [offer] : [])]
            }
        };
        changed = true;
    });

    const extra = [];
    commonKeys.forEach((key) => {
        if (existingKeys.has(key)) return;
        const legVec = basisLegVec({ [key]: 1 });
        const { bid, offer } = impliedBasisSides(legVec, outrightBooks, ctx);
        if (!bid && !offer) return;
        const primaryRow = primaryByKey.get(key);
        const secondaryRow = secondaryByKey.get(key);
        const [startPart, endPart] = family === 'fwd' ? key.split('|') : [null, key];
        const rowKey = `implied|basis${family === 'fwd' ? 'FwdOutright' : 'Outright'}|${key}`;
        enrichedRows[rowKey] = {
            key: rowKey,
            irsId: basisIrs.id,
            currency: basisIrs.currency || 'THB',
            tenor: family === 'fwd'
                ? `${startPart} ${spacedTenor(endPart) || endPart}`
                : spacedTenor(endPart) || endPart,
            endLabel: endPart,
            end: primaryRow?.end || secondaryRow?.end || null,
            startLabel: family === 'fwd' ? startPart : 'Spot',
            start_date: primaryRow?.start_date || secondaryRow?.start_date || null,
            start: family === 'fwd' ? (primaryRow?.start || startPart) : 'Spot',
            orderBook: { bids: bid ? [bid] : [], offers: offer ? [offer] : [] },
            midMatch: [],
            standard: false,
            implied: true,
            isLead: true,
            structureType: family
        };
        extra.push(rowKey);
        changed = true;
    });

    if (!changed) return null;
    const merged = [...basisSection.rowOrder, ...extra]
        .sort((a, b) => yrs(norm(enrichedRows[a]?.endLabel)) - yrs(norm(enrichedRows[b]?.endLabel)));
    return { ...basisSection, rows: enrichedRows, rowOrder: merged };
};

// Enrich one basis spread/fly section (spot 'spreadFly' or fwd 'fwdSpreadFly').
// Decorates every row whose leg tenors are all live-quoted on both siblings;
// fabricates C(n,2) missing pairs. Flies (C(n,3)) are not fabricated —
// desk-curated only.
const enrichBasisSpreadSection = ({ basisSection, primarySection, secondarySection, family, srcFamily, basisIrs, ctx }) => {
    const bctx = buildBasisContext({ primarySection, secondarySection, family: srcFamily });
    if (!bctx) return null;
    const { primaryByKey, secondaryByKey, outrightBooks } = bctx;
    const commonKeys = new Set([...primaryByKey.keys()].filter((k) => secondaryByKey.has(k)));
    if (!commonKeys.size) return null;

    const readLegs = family === 'fwdSpreadFly' ? readFwdSpreadFlyLegs : readSpreadFlyTenors;
    const rows = { ...basisSection.rows };
    const existing = new Set();
    let changed = false;
    basisSection.rowOrder.forEach((rk) => {
        const row = basisSection.rows[rk];
        if (!row) return;
        const byKey = readLegs(row);
        if (!byKey) return;
        const legs = Object.keys(byKey).map(norm);
        existing.add(legs.slice().sort().join('||'));
        if (!legs.every((l) => commonKeys.has(l))) return;
        const legVec = basisLegVec(byKey);
        const { bid, offer } = impliedBasisSides(legVec, outrightBooks, ctx);
        if (!bid && !offer) return;
        rows[rk] = {
            ...row,
            orderBook: {
                ...row.orderBook,
                bids: [...(row.orderBook?.bids || []), ...(bid ? [bid] : [])],
                offers: [...(row.orderBook?.offers || []), ...(offer ? [offer] : [])]
            }
        };
        changed = true;
    });

    // Fabricate C(n,2). For fwd, only within a shared IMM start (basis fwd
    // spread has both legs on the same start); enumerate per start bucket.
    const extra = [];
    const fabricatePair = (nearKey, farKey, near, far, start) => {
        const sig = [nearKey, farKey].sort().join('||');
        if (existing.has(sig)) return;
        const byKey = { [nearKey]: -1, [farKey]: 1 };
        const legVec = basisLegVec(byKey);
        const { bid, offer } = impliedBasisSides(legVec, outrightBooks, ctx);
        if (!bid && !offer) return;
        const nearRow = primaryByKey.get(nearKey) || secondaryByKey.get(nearKey);
        const farRow = primaryByKey.get(farKey) || secondaryByKey.get(farKey);
        const rk = `implied|${family}|${start || ''}|${near}x${far}`;
        rows[rk] = {
            key: rk,
            irsId: basisIrs.id,
            currency: basisIrs.currency || 'THB',
            tenor: start
                ? `${start} ${spacedTenor(near) || near} x ${spacedTenor(far) || far}`
                : `${spacedTenor(near) || near} x ${spacedTenor(far) || far}`,
            end1Label: near,
            endLabel: far,
            end1: nearRow?.end || null,
            end: farRow?.end || null,
            startLabel: start || 'Spot',
            start_date: nearRow?.start_date || null,
            start: start || 'Spot',
            orderBook: { bids: bid ? [bid] : [], offers: offer ? [offer] : [] },
            midMatch: [],
            standard: false,
            implied: true,
            isLead: true,
            structureType: family
        };
        extra.push(rk);
        changed = true;
    };
    if (family === 'fwdSpreadFly') {
        // Group common keys by IMM start.
        const byStart = new Map();
        commonKeys.forEach((k) => {
            const [s, t] = k.split('|');
            if (!s || !t) return;
            if (!byStart.has(s)) byStart.set(s, []);
            byStart.get(s).push(t);
        });
        byStart.forEach((tenors, start) => {
            const sorted = tenors.slice().sort((a, b) => yrs(a) - yrs(b));
            for (let i = 0; i < sorted.length; i += 1) {
                for (let j = i + 1; j < sorted.length; j += 1) {
                    fabricatePair(`${start}|${sorted[i]}`, `${start}|${sorted[j]}`, sorted[i], sorted[j], start);
                }
            }
        });
    } else {
        const commonList = [...commonKeys].sort((a, b) => yrs(a) - yrs(b));
        for (let i = 0; i < commonList.length; i += 1) {
            for (let j = i + 1; j < commonList.length; j += 1) {
                fabricatePair(commonList[i], commonList[j], commonList[i], commonList[j], null);
            }
        }
    }

    if (!changed) return null;
    return { ...basisSection, rows, rowOrder: [...basisSection.rowOrder, ...extra] };
};

// Section families we enrich on the basis IRS. `srcFamily` names the sibling
// outright family the row sources from (a spot spread pulls spot outright
// books; a fwd spread pulls fwd outright books).
const BASIS_SECTION_FAMILIES = [
    { kind: 'outright', family: 'outright', srcFamily: 'outright' },
    { kind: 'outright', family: 'fwd', srcFamily: 'fwd' },
    { kind: 'spread', family: 'spreadFly', srcFamily: 'outright' },
    { kind: 'spread', family: 'fwdSpreadFly', srcFamily: 'fwd' }
];

// -----------------------------------------------------------------------
// Cross-curve enrichment: derive an OIS or NDOIS outright from its sibling
// curve + the basis (§11.1 / §11.2 of the technical spec).
//
// basis = D - ND  ⇒  ND = D - basis  ⇒  D = ND + basis
//
// Recipes (per side rule §7.1):
//   ND(t) bid   =  D(t) bid   - basis(t) offer   [+1 D bid, -1 basis offer]
//   ND(t) offer =  D(t) offer - basis(t) bid     [+1 D offer, -1 basis bid]
//   D(t) bid    =  ND(t) bid  + basis(t) bid     [+1 ND bid, +1 basis bid]
//   D(t) offer  =  ND(t) offer + basis(t) offer  [+1 ND offer, +1 basis offer]
//
// The basis leg is quoted in bp; the sibling leg is quoted in percent. Route
// each into its own sub-sum via `sourceIsOutright` so `priceRecipe` combines
// them under the target's unit (percent for an outright target).
// -----------------------------------------------------------------------
const findOutrightRowByKey = (section, family, key) => {
    if (!section) return null;
    for (const rk of section.rowOrder || []) {
        const r = section.rows[rk];
        if (!r || r.start1Label) continue;
        if (outrightRowKey(r, family) === key) return r;
    }
    return null;
};

const crossCurveOutrightLevels = ({ family, key, targetSide, siblingSection, basisSection, siblingCurve, basisCurve, ctx }) => {
    if (!siblingSection || !basisSection) return { bid: null, offer: null };
    const sibRow = findOutrightRowByKey(siblingSection, family, key);
    const basisRow = findOutrightRowByKey(basisSection, family, key);
    if (!sibRow || !basisRow) return { bid: null, offer: null };
    const sibBook = sibRow.orderBook || { bids: [], offers: [] };
    const basisBook = basisRow.orderBook || { bids: [], offers: [] };
    // ND-target: sibling is D, coefficient on basis is -1.
    // D-target : sibling is ND, coefficient on basis is +1.
    const basisCoef = targetSide === 'nd' ? -1 : 1;
    const priceOne = (side) => {
        const recipe = [
            { id: `sib:${key}`, label: key, curve: siblingCurve || null, book: sibBook, c: 1, sourceIsOutright: true, sectionType: family },
            { id: `basis:${key}`, label: key, curve: basisCurve || null, book: basisBook, c: basisCoef, sourceIsOutright: false, sectionType: family }
        ];
        // Target is an outright quoted in percent — sectionType 'outright' or
        // 'fwd' keeps priceRecipe's targetIsBp = false.
        return priceRecipe(recipe, side, family, ctx);
    };
    const bidWinner = bestWinner(priceOne('bids'), 'bids');
    const offerWinner = bestWinner(priceOne('offers'), 'offers');
    return {
        bid: bidWinner ? shapeLevel(bidWinner, family, 'bids') : null,
        offer: offerWinner ? shapeLevel(offerWinner, family, 'offers') : null
    };
};

// Cross-curve spread/fly enrichment: derive a sibling spread or butterfly
// from the OTHER sibling's spread + the basis spread. Vector algebra:
//   basis_spread(a,b) = D_spread(a,b) - ND_spread(a,b)
//   ⇒ ND_spread = D_spread - basis_spread
//   ⇒ D_spread  = ND_spread + basis_spread
// Same for flies. Two-leg recipe using both source spread rows.
const spreadRowSignature = (row, family) => {
    if (family === 'fwdSpreadFly') {
        const s = norm(row?.startLabel);
        const legs = sortLegs([row.end1Label, row.end2Label, row.endLabel]);
        if (!s || legs.length < 2) return null;
        return `${s}|${legs.join(',')}`;
    }
    const legs = sortLegs([row.end1Label, row.end2Label, row.endLabel]);
    if (legs.length < 2) return null;
    return legs.join(',');
};

const findSpreadRow = (section, signature, family) => {
    if (!section) return null;
    for (const rk of section.rowOrder || []) {
        const r = section.rows[rk];
        if (!r) continue;
        if (spreadRowSignature(r, family) === signature) return r;
    }
    return null;
};

const crossCurveSpreadLevels = ({ family, signature, targetSide, siblingSection, basisSection, siblingCurve, basisCurve, ctx }) => {
    const sibRow = findSpreadRow(siblingSection, signature, family);
    const basisRow = findSpreadRow(basisSection, signature, family);
    if (!sibRow || !basisRow) return { bid: null, offer: null };
    const sibBook = sibRow.orderBook || { bids: [], offers: [] };
    const basisBook = basisRow.orderBook || { bids: [], offers: [] };
    // ND target = D sibling - basis; D target = ND sibling + basis.
    const basisCoef = targetSide === 'nd' ? -1 : 1;
    const priceOne = (side) => {
        const recipe = [
            { id: `sib:${signature}`, label: signature, curve: siblingCurve || null, book: sibBook, c: 1, sourceIsOutright: false, sectionType: family },
            { id: `basis:${signature}`, label: signature, curve: basisCurve || null, book: basisBook, c: basisCoef, sourceIsOutright: false, sectionType: family }
        ];
        return priceRecipe(recipe, side, family, ctx);
    };
    const bidWinner = bestWinner(priceOne('bids'), 'bids');
    const offerWinner = bestWinner(priceOne('offers'), 'offers');
    return {
        bid: bidWinner ? shapeLevel(bidWinner, family, 'bids') : null,
        offer: offerWinner ? shapeLevel(offerWinner, family, 'offers') : null
    };
};

const enrichSiblingSpreadSection = ({ targetSection, siblingSection, basisSection, family, targetSide, siblingCurve, basisCurve, ctx }) => {
    if (!targetSection || !siblingSection || !basisSection) return null;
    const rows = { ...targetSection.rows };
    let changed = false;
    targetSection.rowOrder.forEach((rk) => {
        const row = targetSection.rows[rk];
        const sig = spreadRowSignature(row, family);
        if (!sig) return;
        const { bid, offer } = crossCurveSpreadLevels({ family, signature: sig, targetSide, siblingSection, basisSection, siblingCurve, basisCurve, ctx });
        if (!bid && !offer) return;
        rows[rk] = {
            ...row,
            orderBook: {
                ...row.orderBook,
                bids: [...(row.orderBook?.bids || []), ...(bid ? [bid] : [])],
                offers: [...(row.orderBook?.offers || []), ...(offer ? [offer] : [])]
            }
        };
        changed = true;
    });
    if (!changed) return null;
    return { ...targetSection, rows };
};

// Enrich a sibling (OIS or NDOIS) outright section: for each row whose tenor
// (or composite key for fwd) is also live on the other sibling AND on the
// basis IRS's own outright section, append implied levels sourced from that
// pair. Missing-tenor fabrication is out of scope here — an ND/OIS row that
// doesn't exist in the store is a curated omission, not a hole to fill.
const enrichSiblingOutrightSection = ({ targetSection, siblingSection, basisSection, family, targetSide, siblingCurve, basisCurve, ctx }) => {
    if (!targetSection || !siblingSection || !basisSection) return null;
    const rows = { ...targetSection.rows };
    let changed = false;
    targetSection.rowOrder.forEach((rk) => {
        const row = targetSection.rows[rk];
        if (!row || row.start1Label) return;
        const key = outrightRowKey(row, family);
        if (!key) return;
        const { bid, offer } = crossCurveOutrightLevels({ family, key, targetSide, siblingSection, basisSection, siblingCurve, basisCurve, ctx });
        if (!bid && !offer) return;
        rows[rk] = {
            ...row,
            orderBook: {
                ...row.orderBook,
                bids: [...(row.orderBook?.bids || []), ...(bid ? [bid] : [])],
                offers: [...(row.orderBook?.offers || []), ...(offer ? [offer] : [])]
            }
        };
        changed = true;
    });
    if (!changed) return null;
    return { ...targetSection, rows };
};

// Public entry point. Transforms the groups array in place: for each
// CUSTOM_CURVE_GROUP_CFG whose three IRSes are selected, walks every basis
// section family that both siblings also emit and enriches it. Does NOT create
// new sections — no basis section in the store means no-op for that family.
export const enrichBasisCurveGroups = ({ groups, allIrs, selectedIrsIds, ctx = {} }) => {
    if (!Array.isArray(groups) || !groups.length) return groups || [];
    if (!Array.isArray(allIrs) || !allIrs.length) return groups;
    if (!Array.isArray(selectedIrsIds) || !selectedIrsIds.length) return groups;
    const selected = new Set(selectedIrsIds);
    const irsByTicker = new Map(allIrs.map((i) => [i.ticker, i]));
    const groupByIrsId = new Map(groups.map((g) => [g.irsId, g]));

    const jobs = [];
    CUSTOM_CURVE_GROUP_CFG.forEach((cfg) => {
        const basisIrs = irsByTicker.get(cfg.basisTicker);
        const primaryIrs = irsByTicker.get(cfg.primaryTicker);
        const secondaryIrs = irsByTicker.get(cfg.secondaryTicker);
        if (!basisIrs || !primaryIrs || !secondaryIrs) return;
        if (!selected.has(basisIrs.id) || !selected.has(primaryIrs.id) || !selected.has(secondaryIrs.id)) return;
        const basisGroup = groupByIrsId.get(basisIrs.id);
        const primaryGroup = groupByIrsId.get(primaryIrs.id);
        const secondaryGroup = groupByIrsId.get(secondaryIrs.id);
        if (!basisGroup || !primaryGroup || !secondaryGroup) return;
        jobs.push({ basisIrs, basisGroup, primaryGroup, secondaryGroup, basisCurve: cfg.basisCurve, primaryCurve: cfg.primaryCurve, secondaryCurve: cfg.secondaryCurve });
    });
    if (!jobs.length) return groups;

    return groups.map((group) => {
        // 1) Basis IRS group: enrich its own basis sections from OIS + NDOIS.
        const basisJob = jobs.find((j) => j.basisIrs.id === group.irsId);
        if (basisJob) {
            const replacements = new Map();
            BASIS_SECTION_FAMILIES.forEach(({ kind, family, srcFamily }) => {
                const basisSection = basisJob.basisGroup.sections.find((s) => s.structureType === family);
                if (!basisSection) return;
                const primarySection = basisJob.primaryGroup.sections.find((s) => s.structureType === srcFamily);
                const secondarySection = basisJob.secondaryGroup.sections.find((s) => s.structureType === srcFamily);
                if (!primarySection || !secondarySection) return;
                const enricher = kind === 'outright' ? enrichBasisOutrightSection : enrichBasisSpreadSection;
                const enriched = enricher({ basisSection, primarySection, secondarySection, family, srcFamily, basisIrs: basisJob.basisIrs, ctx });
                if (enriched) replacements.set(basisSection, enriched);
            });
            if (!replacements.size) return group;
            return { ...group, sections: group.sections.map((s) => replacements.get(s) || s) };
        }
        // 2) Sibling group (OIS or NDOIS): enrich its outright/fwd sections
        //    from the other sibling + basis. Rows 1, 3 (NDOIS via D+basis) and
        //    rows 5, 7 (OIS via ND+basis) of the coverage matrix.
        const sibJob = jobs.find((j) => j.primaryIrsId === group.irsId || j.secondaryIrsId === group.irsId
            || j.primaryGroup.irsId === group.irsId || j.secondaryGroup.irsId === group.irsId);
        if (!sibJob) return group;
        const isPrimary = sibJob.primaryGroup.irsId === group.irsId;
        const targetSide = isPrimary ? 'd' : 'nd'; // primary IRS is D (OIS); secondary is ND (NDOIS)
        const siblingGroup = isPrimary ? sibJob.secondaryGroup : sibJob.primaryGroup;
        const siblingCurve = isPrimary ? sibJob.secondaryCurve : sibJob.primaryCurve;
        const { basisGroup, basisCurve } = sibJob;
        const replacements = new Map();
        ['outright', 'fwd'].forEach((family) => {
            const targetSection = group.sections.find((s) => s.structureType === family);
            const siblingSection = siblingGroup.sections.find((s) => s.structureType === family);
            const basisSection = basisGroup.sections.find((s) => s.structureType === family);
            if (!targetSection || !siblingSection || !basisSection) return;
            const enriched = enrichSiblingOutrightSection({ targetSection, siblingSection, basisSection, family, targetSide, siblingCurve, basisCurve, ctx });
            if (enriched) replacements.set(targetSection, enriched);
        });
        // Rows 2/4/6/8: cross-curve spreads & flies. ND spread = D spread -
        // basis spread; D spread = ND spread + basis spread. Only decorates
        // existing sibling spread rows — no fabrication here.
        ['spreadFly', 'fwdSpreadFly'].forEach((family) => {
            const targetSection = group.sections.find((s) => s.structureType === family);
            const siblingSection = siblingGroup.sections.find((s) => s.structureType === family);
            const basisSection = basisGroup.sections.find((s) => s.structureType === family);
            if (!targetSection || !siblingSection || !basisSection) return;
            const enriched = enrichSiblingSpreadSection({ targetSection, siblingSection, basisSection, family, targetSide, siblingCurve, basisCurve, ctx });
            if (enriched) replacements.set(targetSection, enriched);
        });
        if (!replacements.size) return group;
        return { ...group, sections: group.sections.map((s) => replacements.get(s) || s) };
    });
};

// Test-only export.
export const __test = { yrs, norm, sortLegs, insertRanked, legSig, buildBasisContext, impliedBasisOutrightLevels, basisLegVec, impliedBasisSides, outrightRowKey, readFwdSpreadFlyLegs };
